// ABOUTME: Confirmation modal shown when switching to an environment that has no saved table settings.
// ABOUTME: Asks the user whether to push their current config to the new environment.

import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import { tokens } from "../../../theme";

import { dismissSaveToNewEnvPrompt } from "../../../newapi/global/userLayout/userLayoutSlice";
import { usePutTableSettingsMutation } from "../../../newapi/global/tableSettings/tableSettingsSlice";
import { showToastMessageUpdateRequest } from "../../../utils/toasts";

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";

const MConfirmSaveTableSettings = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const open = useSelector((state) => state.userLayout?.promptSaveToNewEnv ?? false);
    const pendingDocument = useSelector((state) => state.userLayout?.pendingDocument ?? null);

    const [putTableSettings] = usePutTableSettingsMutation();

    let isStandAlone = false;
    if (window.matchMedia("(display-mode: standalone)").matches) {
        isStandAlone = true;
    }

    const handleClose = () => {
        dispatch(dismissSaveToNewEnvPrompt());
    };

    const handleConfirm = async () => {
        if (pendingDocument) {
            try {
                const response = await putTableSettings({
                    columnConfigs: pendingDocument.columnConfigs ?? {},
                    generalSettings: pendingDocument.generalSettings ?? {},
                    tabConfigs: pendingDocument.tabConfigs ?? {},
                }).unwrap();
                showToastMessageUpdateRequest(t, response);
            } catch (err) {
                showToastMessageUpdateRequest(t, { success: false, error: "save_failed" });
            }
        }
        dispatch(dismissSaveToNewEnvPrompt());
    };

    return (
        <SHModal
            open={open}
            onClose={handleClose}
        >
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    alignItems:'center',
                    maxWidth: 500,
                }}
            >
                <Subheading>{t("save_table_settings")}</Subheading>
                <Text center light>{t("save_table_settings_subtitle")}</Text>
            </Box>
            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    textAlign: "center",
                }}
            >
                <Text align="center" sx={{maxWidth:400}}>
                    {t("save_table_settings_content")}
                </Text>
            </Box>
            <Box
                component="footer"
                sx={{
                    display: "flex",
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    alignItems: "center",
                    paddingRight: 3,
                    paddingLeft: 3,
                    zIndex: 1058,
                    position: "relative",
                    paddingTop: 2,
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    justifyContent: "space-between",
                    "@media screen and (max-width: 47.9375em)": {
                        paddingRight: 0,
                        paddingLeft: 0,
                    },
                }}
            >
                <Shbutton
                    isCancel={true}
                    className={"always-visible"}
                    onClick={handleClose}
                    color="primary"
                    variant="text"
                    startIcon={<ArrowRightAltIcon />}
                >
                    {t("cancel")}
                </Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                        className={"group-btn"}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        onClick={handleConfirm}
                        variant="contained"
                    >
                        {t("confirm")}
                    </Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    );
};

export default MConfirmSaveTableSettings;
